import React, { useState, useEffect, useCallback } from 'react';
import { Download, TrendingUp, Users, Package, BarChart3, Filter, Info, Trophy, Calendar } from 'lucide-react';
import { wheelsApi } from '../../services/api';
import { Spin, Wheel } from '../../types';
import toast from 'react-hot-toast';
import Table from '../../components/Table';
import { LoadingState } from '../../components/LoadingSpinner';
import { usePageTitle } from '../../hooks/usePageTitle';

const LIMITE = 10000;
interface ReportFilters {
  wheel_id?: number;
  start_date?: string;
  end_date?: string;
  page: number;
  limit: number;
}

interface SpinStats {
  total_spins: number;
  unique_users: number;
  products_distributed: number;
  success_rate: number;
}

interface ProductStats {
  product_name: string;
  total_wins: number;
  win_percentage: number;
  product_image?: string;
}

interface DailyStats {
  date: string;
  total_spins: number;
  unique_users: number;
}

const ReportsPage: React.FC = () => {
  usePageTitle('Relatórios');
  const [spins, setSpins] = useState<Spin[]>([]);
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<ReportFilters>({
    page: 1,
    limit: LIMITE
  });

  const [stats, setStats] = useState<SpinStats>({
    total_spins: 0,
    unique_users: 0,
    products_distributed: 0,
    success_rate: 0
  });

  const [productStats, setProductStats] = useState<ProductStats[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);



  const loadWheels = useCallback(async () => {
    try {
      const response = await wheelsApi.getAll({ page: 1, limit: LIMITE });
      setWheels(response.wheels);
    } catch (error: any) {
      console.error('Erro ao carregar roda de prêmios:', error);
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);

      // Se há uma roda selecionada, buscar seus giros
      if (filters.wheel_id) {
        const response = await wheelsApi.getSpins(filters.wheel_id, {
          page: filters.page,
          limit: filters.limit
        });
        setSpins(response.spins || []);

        // Calcular estatísticas básicas dos spins
        calculateStats(response.spins || []);
      } else {
        setSpins([]);
        setStats({
          total_spins: 0,
          unique_users: 0,
          products_distributed: 0,
          success_rate: 0
        });
      }
    } catch (error: any) {
      toast.error('Erro ao carregar relatórios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadWheels();
    loadReports();
  }, [loadWheels, loadReports]);

  useEffect(() => {
    loadReports();
  }, [filters, loadReports]);

  const calculateStats = (spinsData: Spin[]) => {
    if (!spinsData.length) {
      setStats({
        total_spins: 0,
        unique_users: 0,
        products_distributed: 0,
        success_rate: 0
      });
      return;
    }

    const totalSpins = spinsData.length;
    const uniqueUsers = new Set(spinsData.map(spin => spin.whatsapp)).size;
    const productsDistributed = spinsData.filter(spin => spin.product_id).length;
    const successRate = totalSpins > 0 ? (productsDistributed / totalSpins) * 100 : 0;

    setStats({
      total_spins: totalSpins,
      unique_users: uniqueUsers,
      products_distributed: productsDistributed,
      success_rate: Math.round(successRate * 100) / 100
    });

    // Calcular estatísticas de produtos
    const productWins: { [key: string]: { count: number, name: string } } = {};

    spinsData.forEach(spin => {
      // Suporte a múltiplos produtos (sorteios agendados)
      if (spin.product_ids && spin.product_ids.length > 0 && spin.product_names) {
        spin.product_ids.forEach((productId, index) => {
          const key = productId.toString();
          if (!productWins[key]) {
            productWins[key] = { count: 0, name: spin.product_names![index] || `Produto ${productId}` };
          }
          productWins[key].count++;
        });
      }
      // Produto único (instant draw)
      else if (spin.product_id && spin.product_name) {
        const key = spin.product_id.toString();
        if (!productWins[key]) {
          productWins[key] = { count: 0, name: spin.product_name };
        }
        productWins[key].count++;
      }
    });

    const productStatsArray = Object.values(productWins).map(product => ({
      product_name: product.name,
      total_wins: product.count,
      win_percentage: Math.round((product.count / productsDistributed) * 100 * 100) / 100
    })).sort((a, b) => b.total_wins - a.total_wins);

    setProductStats(productStatsArray);

    // Calcular estatísticas diárias
    const dailyData: { [key: string]: { spins: number, users: Set<string> } } = {};

    spinsData.forEach(spin => {
      const date = new Date(spin.created_at || '').toISOString().split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = { spins: 0, users: new Set() };
      }
      dailyData[date].spins++;
      dailyData[date].users.add(spin.whatsapp);
    });

    const dailyStatsArray = Object.entries(dailyData)
      .map(([date, data]) => ({
        date,
        total_spins: data.spins,
        unique_users: data.users.size
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    setDailyStats(dailyStatsArray);
  };

  const handleFilterChange = (field: keyof ReportFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset page when filters change
    }));
  };

  const exportToCSV = () => {
    if (!spins.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    // Buscar configuração dos custom_fields da roda selecionada
    const selectedWheel = wheels.find(w => w.id === filters.wheel_id);
    const customFields = selectedWheel?.custom_fields || [];

    // Coletar todos os campos customizados únicos e mapear para labels
    const customFieldsMap = new Map<string, string>();

    spins.forEach(spin => {
      if (spin.custom_data) {
        Object.keys(spin.custom_data).forEach(fieldId => {
          if (!customFieldsMap.has(fieldId)) {
            const field = customFields.find((f: any) => f.id === fieldId);
            const label = field?.label || fieldId;
            customFieldsMap.set(fieldId, label);
          }
        });
      }
    });

    const customFieldIds = Array.from(customFieldsMap.keys());
    const customFieldLabels = customFieldIds.map(id => customFieldsMap.get(id) || id);

    const headers = [
      'Data/Hora',
      'Nome',
      'WhatsApp',
      'Produto Ganho',
      'Status',
      ...customFieldLabels
    ];

    const csvData = spins.map(spin => [
      new Date(spin.created_at || '').toLocaleString('pt-BR'),
      spin.customer_name,
      spin.whatsapp,
      // Múltiplos produtos separados por ponto e vírgula
      spin.product_ids && spin.product_ids.length > 1
        ? (spin.product_names?.join('; ') || `${spin.product_ids.length} prêmios`)
        : (spin.product_name || 'Nenhum'),
      spin.product_id || (spin.product_ids && spin.product_ids.length > 0) ? 'Ganhou' : 'Não ganhou',
      ...customFieldIds.map(fieldId => spin.custom_data?.[fieldId] || '')
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-giros-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Relatório exportado com sucesso!');
  };

  const spinsColumns = [
    {
      header: 'Data/Hora',
      accessor: (spin: Spin) => (
        new Date(spin.created_at || '').toLocaleString('pt-BR')
      )
    },
    {
      header: 'Nome',
      accessor: 'customer_name' as keyof Spin,
      className: 'font-medium'
    },
    {
      header: 'WhatsApp',
      accessor: 'whatsapp' as keyof Spin
    },
    {
      header: 'Produto Ganho',
      accessor: (spin: Spin) => {
        // Múltiplos produtos (sorteio agendado)
        if (spin.product_ids && spin.product_ids.length > 1) {
          return (
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-musgo rounded-full"></span>
              <span className="text-musgo font-semibold">
                {spin.product_ids.length} prêmios
              </span>
            </div>
          );
        }
        // Produto único
        if (spin.product_id) {
          return (
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-musgo rounded-full"></span>
              <span className="text-musgo font-semibold">{spin.product_name}</span>
            </div>
          );
        }
        // Sem produto
        return (
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 bg-tinta-soft/40 rounded-full"></span>
            <span className="text-tinta-soft italic">Nenhum</span>
          </div>
        );
      }
    },
    {
      header: 'Status',
      accessor: (spin: Spin) => (
        <span className={`px-2 py-1 text-xs rounded-full ${spin.product_id
          ? 'bg-musgo/15 text-musgo border border-musgo/30 font-bold'
          : 'bg-tinta/8 text-tinta-soft border border-tinta/15'
          }`}>
          {spin.product_id ? 'Ganhou' : 'Não ganhou'}
        </span>
      )
    },
    {
      header: 'Dados Adicionais',
      accessor: (spin: Spin) => {
        if (!spin.custom_data || Object.keys(spin.custom_data).length === 0) {
          return <span className="text-xs text-tinta-soft/60 italic">-</span>;
        }

        // Buscar configuração dos custom_fields da roda selecionada
        const selectedWheel = wheels.find(w => w.id === filters.wheel_id);
        const customFields = selectedWheel?.custom_fields || [];

        // Mapear field IDs para labels
        const dataEntries = Object.entries(spin.custom_data)
          .map(([fieldId, value]) => {
            const field = customFields.find((f: any) => f.id === fieldId);
            const label = field?.label || fieldId;
            return { label, value };
          })
          .filter(entry => entry.value);

        if (dataEntries.length === 0) {
          return <span className="text-xs text-tinta-soft/60 italic">-</span>;
        }

        return (
          <div className="text-xs text-tinta-soft space-y-0.5">
            {dataEntries.slice(0, 2).map((entry, idx) => (
              <div key={idx} className="truncate max-w-[150px]" title={`${entry.label}: ${entry.value}`}>
                <span className="font-bold text-tinta">{entry.label}:</span> {String(entry.value)}
              </div>
            ))}
            {dataEntries.length > 2 && (
              <div className="text-tinta-soft/60 italic">
                +{dataEntries.length - 2} mais
              </div>
            )}
          </div>
        );
      }
    }
  ];

  if (loading && !filters.wheel_id) {
    return <LoadingState message="Carregando relatórios..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-tinta leading-tight">
            Relatórios e <span className="italic text-bordo">insights</span>
          </h1>
          <p className="mt-2 text-sm text-tinta-soft">
            Analise o desempenho dos seus sorteios.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-bold border transition-colors ${
              showFilters
                ? 'text-bordo bg-bordo/8 border-bordo/40'
                : 'text-tinta-soft bg-white border-tinta/15 hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filtros
          </button>
          <button
            onClick={exportToCSV}
            disabled={!spins.length}
            className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark disabled:opacity-50 transition-colors"
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border border-tinta/10 rounded-xl shadow-sm p-4 sm:p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Sorteio
              </label>
              <select
                value={filters.wheel_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    handleFilterChange('wheel_id', undefined);
                  } else if (value === 'manual') {
                    handleFilterChange('wheel_id', 'manual');
                  } else {
                    handleFilterChange('wheel_id', parseInt(value));
                  }
                }}
                className="block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
              >
                <option value="">Selecione um sorteio</option>
                <option value="manual">Sorteio Manual</option>
                {wheels.map(wheel => (
                  <option key={wheel.id} value={wheel.id}>
                    {wheel.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Data Inicial
              </label>
              <input
                type="date"
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Data Final
              </label>
              <input
                type="date"
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
              />
            </div>
          </div>
        </div>
      )}

      {/* Alert quando nenhuma roda está selecionada */}
      {!filters.wheel_id && (
        <div className="flex gap-3 p-4 rounded-lg bg-tinta/5 border border-tinta/15">
          <Info className="w-5 h-5 text-tinta-soft mt-0.5 flex-shrink-0" />
          <p className="text-sm text-tinta">Selecione uma roda nos filtros para visualizar os relatórios detalhados.</p>
        </div>
      )}

      {/* Stats Cards */}
      {filters.wheel_id && (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {/* Total de Giros */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-bordo" />
                </div>
              </div>
              <p className="font-display text-3xl font-black text-tinta">{stats.total_spins.toLocaleString()}</p>
              <p className="text-sm text-tinta-soft mt-1">Total de Giros</p>
            </div>

            {/* Usuários Únicos */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-musgo/10 border border-musgo/20 flex items-center justify-center">
                  <Users className="h-5 w-5 text-musgo" />
                </div>
              </div>
              <p className="font-display text-3xl font-black text-tinta">{stats.unique_users.toLocaleString()}</p>
              <p className="text-sm text-tinta-soft mt-1">Usuários Únicos</p>
            </div>

            {/* Produtos Distribuídos */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-mostarda/20 border border-mostarda/40 flex items-center justify-center">
                  <Package className="h-5 w-5 text-mostarda-dark" />
                </div>
              </div>
              <p className="font-display text-3xl font-black text-tinta">{stats.products_distributed.toLocaleString()}</p>
              <p className="text-sm text-tinta-soft mt-1">Produtos Distribuídos</p>
            </div>

            {/* Taxa de Sucesso */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-bordo" />
                </div>
              </div>
              <p className="font-display text-3xl font-black text-tinta">{stats.success_rate}%</p>
              <p className="text-sm text-tinta-soft mt-1">Taxa de Sucesso</p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
              <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2 mb-4">
                <Trophy className="h-5 w-5 text-mostarda-dark" />
                Produtos Mais Sorteados
              </h3>
              {productStats.length > 0 ? (
                <div className="space-y-3">
                  {productStats.slice(0, 5).map((product, index) => {
                    const barColor = index === 0 ? 'bg-mostarda'
                      : index === 1 ? 'bg-tinta-soft/50'
                        : index === 2 ? 'bg-bordo-dark'
                          : 'bg-bordo';
                    return (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="text-sm text-tinta truncate">
                            {index === 0 && '🥇 '}
                            {index === 1 && '🥈 '}
                            {index === 2 && '🥉 '}
                            {product.product_name}
                          </div>
                          <div className="text-xs text-tinta-soft italic">
                            {product.total_wins} vezes ({product.win_percentage}%)
                          </div>
                        </div>
                        <div className="w-24 bg-tinta/8 rounded-full overflow-hidden h-2">
                          <div
                            className={`${barColor} h-2 rounded-full transition-all`}
                            style={{ width: `${Math.min(product.win_percentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-4 italic text-tinta-soft">
                  Nenhum produto sorteado ainda
                </p>
              )}
            </div>

            {/* Daily Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
              <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5 text-bordo" />
                Atividade Diária (Últimos 7 dias)
              </h3>
              {dailyStats.length > 0 ? (
                <div>
                  {dailyStats.slice(-7).map((day, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-tinta/8 last:border-0">
                      <div className="text-sm font-semibold text-tinta">
                        {new Date(day.date).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-right">
                        <div className="font-display text-sm font-bold text-bordo">
                          {day.total_spins} giros
                        </div>
                        <div className="text-xs text-tinta-soft italic">
                          {day.unique_users} usuários
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 italic text-tinta-soft">
                  Nenhuma atividade registrada
                </p>
              )}
            </div>
          </div>

          {/* Spins Table */}
          <div className="bg-white rounded-xl shadow-sm border border-tinta/10 overflow-hidden">
            <div className="px-6 py-4 border-b border-tinta/10">
              <h3 className="font-display text-lg font-bold text-tinta">
                Histórico de Giros
              </h3>
            </div>
            <div className="p-6">
              <Table
                data={spins}
                columns={spinsColumns}
                loading={loading}
                emptyMessage="Nenhum giro encontrado para os filtros selecionados"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
